import { Controller, UseFormClearErrors } from "react-hook-form";
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Device,
  DOC_TYPE_ID,
  INVALID_PHOTO_ERRORS,
  STATUS_IDS,
  Visitor,
} from "../../constants/common";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import {
  useGetAllNationalities,
  useGetDocumentIDTypes,
  useGetValidatePhoto,
} from "../../hooks/common/query";
import { useTranslation } from "react-i18next";
import {
  fileToBase64,
  formattedDate,
  mapToOptions,
  onlyDate,
} from "../../lib/functions";
import TextInput from "../inputs/TextInput";
import ComboboxInput from "../inputs/ComboboxInput";
import { Button } from "../button";
import AttachmentsGroupCard from "./AttachmentsGroupCard";
import { createVisitorDetailsGroupCardSchema } from "../../schemas/visitorDetailsGroupCardSchema";
import DatePickerInput from "../inputs/DatePickerInput";
import { APP_ROUTES } from "../../routes";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useValidatePhoto } from "../../hooks/visitor/mutation";
import CheckboxInput from "../inputs/CheckboxInput";
import GroupVisitorDeviceDetailsCard from "./GroupVisitorDeviceDetailsCard";

interface VisitorDetailsGroupCardProps {
  setIsAddVisitorFormVisible: React.Dispatch<React.SetStateAction<boolean>>;

  setAddedVisitors: React.Dispatch<React.SetStateAction<Visitor[]>>;
  // isVehicleInfoVisible: boolean;

  uploadPhotoRef: React.MutableRefObject<HTMLInputElement | null>;
  photoFileName: string;
  setPhotoFileName: React.Dispatch<React.SetStateAction<string>>;
  setPhotoFileType: Dispatch<SetStateAction<string>>;

  uploadEidRef: React.MutableRefObject<HTMLInputElement | null>;
  eidFileName: string;
  setEidFileName: React.Dispatch<React.SetStateAction<string>>;
  setEidFileType: Dispatch<SetStateAction<string>>;

  uploadPassportRef: MutableRefObject<HTMLInputElement | null>;
  passportFileName: string;
  setPassportFileName: Dispatch<SetStateAction<string>>;
  setPassportFileType: Dispatch<SetStateAction<string>>;

  uploadVisaRef: MutableRefObject<HTMLInputElement | null>;
  visaFileName: string;
  setVisaFileName: Dispatch<SetStateAction<string>>;
  setVisaFileType: Dispatch<SetStateAction<string>>;

  iqamaFileType: string;
  eidFileType: string;
  passportFileType: string;
  photoFileType: string;
  idOtherFileType: string;
  vehicleRegLicenseFileType: string;
  visaFileType?: string;

  isVip?: boolean;

  uploadVehicleRegLicenseRef: MutableRefObject<HTMLInputElement | null>;
  setVehicleRegLicenseFileName: Dispatch<SetStateAction<string>>;
  setVehicleRegLicenseFileType: Dispatch<SetStateAction<string>>;
  vehicleRegLicenseFileName: string;

  uploadIqamaRef: React.MutableRefObject<HTMLInputElement | null>;
  iqamaFileName: string;
  setIqamaFileName: React.Dispatch<React.SetStateAction<string>>;
  setIqamaFileType: Dispatch<SetStateAction<string>>;

  uploadIdOtherRef: React.MutableRefObject<HTMLInputElement | null>;
  idOtherFileName: string;
  setIdOtherFileName: React.Dispatch<React.SetStateAction<string>>;
  setIdOtherFileType: Dispatch<SetStateAction<string>>;

  uploadHostEmployeeCardRef: MutableRefObject<HTMLInputElement | null>;
  hostEmployeeCardFileName: string;
  hostEmployeeCardFileType: string;
  setHostEmployeeCardFileName: Dispatch<SetStateAction<string>>;
  setHostEmployeeCardFileType: Dispatch<SetStateAction<string>>;

  visitEndDate: Date;

  editingVisitor: Visitor | null;
  setEditingVisitor: React.Dispatch<React.SetStateAction<Visitor | null>>;

  clearErrors: UseFormClearErrors<{
    visitingDepartment?: string | undefined;
    gate?: string | undefined;
    meetingRoom?: string | undefined;
    visitPurpose?: string | undefined;
    visitPurposeOther?: string | undefined;
    plateSource?: string | undefined;
    plateNumber?: string | null | undefined;
    plateLetter1?: string | undefined;
    plateLetter2?: string | undefined;
    plateLetter3?: string | undefined;
    note?: string | undefined;
    location: string;
    building: string;
    visitReqType: string;
    hostEmail: string;
    hostMobileNumber: string;
    visitStartDate: string;
    visitEndDate: string;
  }>;
}

const VisitorDetailsGroupCard = ({
  setAddedVisitors,
  setIsAddVisitorFormVisible,
  uploadPhotoRef,
  photoFileName,
  setPhotoFileName,
  setPhotoFileType,
  uploadEidRef,
  eidFileName,
  setEidFileName,
  setEidFileType,
  uploadPassportRef,
  passportFileName,
  setPassportFileName,
  setPassportFileType,
  eidFileType,
  passportFileType,
  photoFileType,
  isVip,

  uploadVisaRef,
  visaFileName,
  setVisaFileName,
  setVisaFileType,
  visaFileType,

  uploadVehicleRegLicenseRef,
  setVehicleRegLicenseFileName,
  setVehicleRegLicenseFileType,
  vehicleRegLicenseFileName,
  vehicleRegLicenseFileType,

  uploadIqamaRef,
  iqamaFileName,
  iqamaFileType,
  setIqamaFileName,
  setIqamaFileType,

  uploadIdOtherRef,
  idOtherFileName,
  idOtherFileType,
  setIdOtherFileName,
  setIdOtherFileType,

  uploadHostEmployeeCardRef,
  hostEmployeeCardFileName,
  hostEmployeeCardFileType,
  setHostEmployeeCardFileName,
  setHostEmployeeCardFileType,

  visitEndDate,

  editingVisitor,
  setEditingVisitor,

  clearErrors,
}: VisitorDetailsGroupCardProps) => {
  const [isAttachments, setIsAttachments] = useState({
    havePhoto: 0,
    haveEid: 0,
    havePassport: 0,
    haveVehicleRegistration: 0,
    haveIqama: 0,
    haveOthers: 0,
    haveVisa: 0,
    haveHostEmployeeCard: 0,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useFormWithSchema(
    createVisitorDetailsGroupCardSchema(isAttachments, isVip),
    {
      defaultValues: {
        idType: DOC_TYPE_ID.NATIONAL_ID,
      },
    }
  );

  const location = useLocation();

  const selectedIdType = watch("idType");

  const [nationalities, setNationalities] = useState<
    { labelEn: string; labelAr: string; value: any }[]
  >([]);

  const { data: allNationalities } = useGetAllNationalities();

  const { t } = useTranslation();

  const [isDeviceDetails, setIsDeviceDetails] = useState(false);
  const [addedDevices, setAddedDevices] = useState<Device[]>();

  const [fetchedAppointmentId, setFetchedAppointmentId] = useState<any>();

  useEffect(() => {
    if (editingVisitor) {
      const userData = {
        id: editingVisitor?.id,
        name: editingVisitor?.FullName,
        companyName: editingVisitor?.Sponsor,
        nationality: editingVisitor?.Nationality,
        number: editingVisitor?.MobileNo,
        email: editingVisitor?.Email,

        idType: editingVisitor?.IDType,

        eid:
          editingVisitor?.IDType === DOC_TYPE_ID.NATIONAL_ID &&
          editingVisitor?.EIDNumber
            ? editingVisitor?.EIDNumber
            : "",
        eidExpiryDate:
          editingVisitor?.IDType === DOC_TYPE_ID.NATIONAL_ID
            ? formattedDate(new Date(editingVisitor?.Dt_EIDExpiryDate))
            : "",
        passportNumber:
          editingVisitor?.IDType === DOC_TYPE_ID.PASSPORT &&
          editingVisitor?.PassportNumber
            ? editingVisitor?.PassportNumber
            : "",
        passportExpiryDate:
          editingVisitor?.IDType === DOC_TYPE_ID.PASSPORT
            ? editingVisitor?.Dt_PassportExpiryDate
            : "",

        iqama:
          editingVisitor?.IDType === DOC_TYPE_ID.IQAMA &&
          editingVisitor?.S_Iqama
            ? editingVisitor?.S_Iqama
            : "",
        iqamaExpiryDate:
          editingVisitor?.IDType === DOC_TYPE_ID.IQAMA
            ? editingVisitor?.Dt_IqamaExpiry
            : "",

        idTypeOtherName:
          editingVisitor?.IDType === DOC_TYPE_ID.OTHER
            ? editingVisitor?.S_OthersDoc
            : "",
        idTypeOtherNumber:
          editingVisitor?.IDType === DOC_TYPE_ID.OTHER &&
          editingVisitor?.S_OthersValue
            ? editingVisitor?.S_OthersValue
            : "",
        idTypeOtherExpiryDate:
          editingVisitor?.IDType === DOC_TYPE_ID.OTHER
            ? editingVisitor?.Dt_OthersExpiry
            : "",

        visaNumber:
          editingVisitor?.IDType === DOC_TYPE_ID.VISA &&
          editingVisitor?.S_VisaNo
            ? editingVisitor?.S_VisaNo
            : "",
        visaExpiryDate:
          editingVisitor?.IDType === DOC_TYPE_ID.VISA
            ? formattedDate(new Date(editingVisitor?.Dt_VisaExpiryDate))
            : "",

        dateOfBirth: editingVisitor?.dateOfBirth
          ? editingVisitor?.dateOfBirth
          : "",
      };

      if (userData) {
        reset(userData);
      }
      setFetchedAppointmentId("");

      // if (editingVisitor?.uploadPhoto) {
      //   const file = editingVisitor.uploadPhoto as unknown as File;
      //   setFileName(ATTACHMENT_KEYS.PHOTO, file.name);
      //   setFileType(ATTACHMENT_KEYS.PHOTO, file.type);
      // }
      // if (
      //   editingVisitor?.IDType === DOC_TYPE_ID.NATIONAL_ID &&
      //   editingVisitor?.uploadEid
      // ) {
      //   const file = editingVisitor.uploadEid as unknown as File;
      //   setFileName(ATTACHMENT_KEYS.EID, file.name);
      //   setFileType(ATTACHMENT_KEYS.EID, file.type);
      // }
      // if (
      //   editingVisitor?.IDType === DOC_TYPE_ID.PASSPORT &&
      //   editingVisitor?.uploadPassport
      // ) {
      //   const file = editingVisitor.uploadPassport as unknown as File;
      //   setFileName(ATTACHMENT_KEYS.PASSPORT, file.name);
      //   setFileType(ATTACHMENT_KEYS.PASSPORT, file.type);
      // }
      // if (editingVisitor?.uploadVehicleRegLicense) {
      //   const file = editingVisitor.uploadVehicleRegLicense as unknown as File;
      //   setFileName(ATTACHMENT_KEYS.VEHICLE, file.name);
      //   setFileType(ATTACHMENT_KEYS.VEHICLE, file.type);
      // }
      // if (editingVisitor?.uploadEmployeeInsuranceCopy) {
      //   const file =
      //     editingVisitor.uploadEmployeeInsuranceCopy as unknown as File;
      //   setFileName(ATTACHMENT_KEYS.EMPLOYEE_INSURANCE_COPY, file.name);
      //   setFileType(ATTACHMENT_KEYS.EMPLOYEE_INSURANCE_COPY, file.type);
      // }

      setIsDeviceDetails(
        editingVisitor?.Devices && editingVisitor?.Devices.length > 0
          ? true
          : false
      );

      const fetchedDevices = editingVisitor?.Devices?.map((item: any) => ({
        appointmentDeviceId: item.appointmentDeviceId,
        serialNumber: item.SerialNumber,
        approvalStatus: item.ApprovalStatus,
        deviceModel: item.DeviceModel,
        purpose: item.Purpose,
        devicePurposeOther: item.DevicePurposeOthersValue,
        deviceTypeOther: item.DeviceTypeOthersValue,
        deviceType: item.DeviceType,
      }));

      setAddedDevices(fetchedDevices);

      setIsAttachments({
        havePhoto: editingVisitor?.uploadPhoto ? 1 : 0,
        haveEid:
          editingVisitor?.IDType === DOC_TYPE_ID.NATIONAL_ID &&
          editingVisitor?.uploadEid
            ? 1
            : 0,
        havePassport:
          editingVisitor?.IDType === DOC_TYPE_ID.PASSPORT &&
          editingVisitor?.uploadPassport
            ? 1
            : 0,
        haveVehicleRegistration: editingVisitor?.uploadVehicleRegLicense
          ? 1
          : 0,
        haveIqama: editingVisitor?.uploadIqama ? 1 : 0,
        haveOthers: editingVisitor?.uploadIdOther ? 1 : 0,
        haveVisa: editingVisitor?.uploadVisa ? 1 : 0,
        haveHostEmployeeCard: editingVisitor?.uploadHostEmployeeCard ? 1 : 0,
      });
    }
  }, [editingVisitor, reset]);

  useEffect(() => {
    if (allNationalities) {
      const mappedNationalities = mapToOptions(
        "name",
        "name_Ar",
        "iso3",
        allNationalities
      );
      setNationalities(mappedNationalities);
    }
  }, [allNationalities]);

  const { mutateAsync: ValidatePhoto, isPending: isValidatePhotoLoading } =
    useValidatePhoto();

  const { data: validatePhoto } = useGetValidatePhoto();

  const [isValidatePhoto, setIsValidatePhoto] = useState<boolean>(false);

  const { data: allDocumentIDTypes } = useGetDocumentIDTypes();

  const documentIDTypes = useMemo(
    () =>
      mapToOptions(
        "s_Desc_E",
        "s_Desc_A",
        "n_DetailedCode",
        allDocumentIDTypes
      ),
    [allDocumentIDTypes]
  );

  useEffect(() => {
    if (validatePhoto) {
      setIsValidatePhoto(validatePhoto.isValidatePhotoFromFR);
    }
  }, [validatePhoto]);

  const onSubmit = useCallback(
    async (data: any) => {
      if (isDeviceDetails && (!addedDevices || addedDevices?.length === 0)) {
        toast.error(t("kindlyAddADevice"));
        return;
      }

      if (
        data.iqama &&
        data.idType === DOC_TYPE_ID.IQAMA &&
        onlyDate(data.iqama) < onlyDate(visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.idType === DOC_TYPE_ID.NATIONAL_ID &&
        onlyDate(data.eidExpiryDate) < onlyDate(visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.idType === DOC_TYPE_ID.PASSPORT &&
        onlyDate(data.passportExpiryDate) < onlyDate(visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.idTypeOtherExpiryDate &&
        data.idType === DOC_TYPE_ID.OTHER &&
        onlyDate(data.idTypeOtherExpiryDate) < onlyDate(visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else if (
        data.idType === DOC_TYPE_ID.VISA &&
        onlyDate(data.visaExpiryDate) < onlyDate(visitEndDate)
      ) {
        toast.error(t("expireVisitError"));
      } else {
        let isPhotoValid = false;

        const base64String =
          data.uploadPhoto && (await fileToBase64(data.uploadPhoto));

        if (data.uploadPhoto && isValidatePhoto) {
          try {
            const body = {
              fullName: data.name,
              photo: base64String,
            };

            const res = await ValidatePhoto(body);
            isPhotoValid = res.isPhotoValid;
          } catch (error: any) {
            const { result } = error;
            const errorCode = result.photoErrors[0]?.errorCode;

            const errorMessages: Record<string, string> = {
              [INVALID_PHOTO_ERRORS.PHOTO_VALIDATION_FAILED]: t(
                "photoValidationFailed"
              ),
              [INVALID_PHOTO_ERRORS.PICTURE_FORMAT_ERROR]: t(
                // "The uploaded file format is not supported."
                "errorValidatingPhoto"
              ),
              [INVALID_PHOTO_ERRORS.WRONG_SIZE]: t("photoSizeIncorrect"),
              [INVALID_PHOTO_ERRORS.PICTURE_SIZE_TOO_SMALL]: t("photoTooSmall"),
              [INVALID_PHOTO_ERRORS.RESOLUTION_NO_SUPPORT]: t(
                "photoResolutionNotSupported"
              ),
              [INVALID_PHOTO_ERRORS.FACE_QUALITY_NOT_OK]: t("faceQualityNotOk"),
              [INVALID_PHOTO_ERRORS.NO_PICTURE]: t("noPhotoUploaded"),
              [INVALID_PHOTO_ERRORS.NO_FACE]: t("noFaceDetectedPhoto"),
              [INVALID_PHOTO_ERRORS.MULTIPLE_FACES_DETECTED]: t(
                "multipleFacesDetected"
              ),
              [INVALID_PHOTO_ERRORS.FACE_TOO_SMALL]: t("faceTooSmallPhoto"),
              [INVALID_PHOTO_ERRORS.PICTURE_TOO_BRIGHT]: t("photoTooBright"),
              [INVALID_PHOTO_ERRORS.PICTURE_TOO_DARK]: t("photoTooDark"),
              [INVALID_PHOTO_ERRORS.FACE_TOO_BLUR]: t("faceTooBlurry"),
              [INVALID_PHOTO_ERRORS.WRONG_FACE_POSE]: t(
                "faceNotProperlyAligned"
              ),
              [INVALID_PHOTO_ERRORS.DUPLICATE_FACE_IMAGE]:
                t("photoAlreadyUsed"),
              [INVALID_PHOTO_ERRORS.DUPLICATE_FACE_NUMBER]: t(
                "photoFaceAlreadyExists"
              ),
              [INVALID_PHOTO_ERRORS.DUPLICATE_FACE_NAME]: t(
                "faceAlreadyRegisteredSameName"
              ),
            };

            toast.error(errorMessages[errorCode] ?? t("somethingWentWrong"));
            return;
          }
        }

        if (data.uploadPhoto && isValidatePhoto && !isPhotoValid) {
          return;
        }

          const details = {
            FullName: data.name,
            Sponsor: data.companyName,
            MobileNo: data.number,
            Email: data.email,
            dateOfBirth: data.dateOfBirth,
            IDType: Number(data.idType),

            S_Iqama: data.idType === DOC_TYPE_ID.IQAMA ? data.iqama : "",
            Dt_IqamaExpiry:
              data.idType === DOC_TYPE_ID.IQAMA ? data.iqamaExpiryDate : null,

            EIDNumber: data.idType === DOC_TYPE_ID.NATIONAL_ID ? data.eid : "",

            PassportNumber:
              data.idType === DOC_TYPE_ID.PASSPORT ? data.passportNumber : "",

            S_VisaNo: data.idType === DOC_TYPE_ID.VISA ? data.visaNumber : "",

            Nationality: data.nationality,

            S_VehicleNo: data.vehicleNo,

            Dt_EIDExpiryDate:
              data.idType === DOC_TYPE_ID.NATIONAL_ID
                ? data.eidExpiryDate
                : null,
            Dt_PassportExpiryDate:
              data.idType === DOC_TYPE_ID.PASSPORT
                ? data.passportExpiryDate
                : null,
            Dt_VisaExpiryDate:
              data.idType === DOC_TYPE_ID.VISA ? data.visaExpiryDate : null,
            S_OthersDoc:
              data.idType === DOC_TYPE_ID.OTHER ? data.idTypeOtherName : "",
            S_OthersValue:
              data.idType === DOC_TYPE_ID.OTHER ? data.idTypeOtherNumber : "",
            Dt_OthersExpiry:
              data.idType === DOC_TYPE_ID.OTHER
                ? data.idTypeOtherExpiryDate
                : null,

            uploadPhoto: data.uploadPhoto,
            photoFileType: photoFileType,

            uploadEid:
              data.idType === DOC_TYPE_ID.NATIONAL_ID ? data.uploadEid : "",
            eidFileType:
              data.idType === DOC_TYPE_ID.NATIONAL_ID ? eidFileType : "",

            uploadIqama:
              data.idType === DOC_TYPE_ID.IQAMA ? data.uploadIqama : "",
            iqamaFileType:
              data.idType === DOC_TYPE_ID.IQAMA ? iqamaFileType : "",

            uploadIdOther:
              data.idType === DOC_TYPE_ID.OTHER ? data.uploadIdOther : "",
            idOtherFileType:
              data.idType === DOC_TYPE_ID.OTHER ? idOtherFileType : "",

            uploadPassport:
              data.idType === DOC_TYPE_ID.PASSPORT ? data.uploadPassport : "",
            passportFileType:
              data.idType === DOC_TYPE_ID.PASSPORT ? passportFileType : "",

            uploadVisa: data.idType === DOC_TYPE_ID.VISA ? data.uploadVisa : "",
            visaFileType: data.idType === DOC_TYPE_ID.VISA ? visaFileType : "",

            uploadVehicleRegLicense: data.uploadVehicleRegLicense,
            vehicleRegLicenseFileType: vehicleRegLicenseFileType,

            uploadHostEmployeeCard: data.uploadHostEmployeeCard,
            hostEmployeeCardFileType: hostEmployeeCardFileType,

            Devices: isDeviceDetails
              ? addedDevices?.map((item: any) => ({
                  appointmentDeviceId: item.appointmentDeviceId || 0,
                  DeviceType: item.deviceType,
                  DeviceTypeOthersValue: item.deviceTypeOther,
                  DeviceModel: item.deviceModel,
                  SerialNumber: item.serialNumber,
                  // DevicePurpose: item.devicePurpose,
                  Purpose: item.purpose,
                  DevicePurposeOthersValue: item.devicePurposeOther,
                  ApprovalStatus: STATUS_IDS.PENDING,
                }))
              : null,
          };
          const id = uuidv4();

          if (editingVisitor) {
            setAddedVisitors((prev) =>
              prev.map((visitor) =>
                visitor.id === editingVisitor.id
                  ? {
                      id: editingVisitor.id,
                      ...(fetchedAppointmentId && {
                        LastAppointmentId: fetchedAppointmentId,
                      }),
                      ...details,
                      // Preserve existing file unless explicitly removed (null) or replaced (File)
                      uploadPhoto:
                        data.uploadPhoto !== undefined ? data.uploadPhoto : editingVisitor.uploadPhoto,
                      uploadEid:
                        data.idType === DOC_TYPE_ID.NATIONAL_ID
                          ? data.uploadEid !== undefined ? data.uploadEid : editingVisitor.uploadEid
                          : "",
                      uploadPassport:
                        data.idType === DOC_TYPE_ID.PASSPORT
                          ? data.uploadPassport !== undefined ? data.uploadPassport : editingVisitor.uploadPassport
                          : "",
                      uploadVehicleRegLicense:
                        data.uploadVehicleRegLicense !== undefined ? data.uploadVehicleRegLicense : editingVisitor.uploadVehicleRegLicense,
                      uploadHostEmployeeCard:
                        data.uploadHostEmployeeCard !== undefined ? data.uploadHostEmployeeCard : editingVisitor.uploadHostEmployeeCard,
                      uploadIqama:
                        data.idType === DOC_TYPE_ID.IQAMA
                          ? data.uploadIqama !== undefined ? data.uploadIqama : editingVisitor.uploadIqama
                          : "",
                      uploadIdOther:
                        data.idType === DOC_TYPE_ID.OTHER
                          ? data.uploadIdOther !== undefined ? data.uploadIdOther : editingVisitor.uploadIdOther
                          : "",
                      uploadVisa:
                        data.idType === DOC_TYPE_ID.VISA
                          ? data.uploadVisa !== undefined ? data.uploadVisa : editingVisitor.uploadVisa
                          : "",
                    }
                  : visitor
              )
            );
            setEditingVisitor(null);
          } else {
            setAddedVisitors((prev) => [
              ...prev,
              {
                id,
                ...details,
              },
            ]);
          }

          reset();
          setPhotoFileName("");
          setEidFileName("");
          setPassportFileName("");
          setIqamaFileName("");
          setVisaFileName("");
          setIdOtherFileName("");
          setIsAddVisitorFormVisible(false);

          setIsDeviceDetails(false);
          setAddedDevices([]);
      }
    },
    [
      isDeviceDetails,
      addedDevices,
      visitEndDate,
      t,
      isValidatePhoto,
      ValidatePhoto,
      photoFileType,
      eidFileType,
      iqamaFileType,
      idOtherFileType,
      passportFileType,
      visaFileType,
      vehicleRegLicenseFileType,
      hostEmployeeCardFileType,
      editingVisitor,
      reset,
      setPhotoFileName,
      setEidFileName,
      setPassportFileName,
      setIqamaFileName,
      setVisaFileName,
      setIdOtherFileName,
      setIsAddVisitorFormVisible,
      setAddedVisitors,
      setEditingVisitor,
      fetchedAppointmentId,
    ]
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
        <Controller
          name="name"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label={t("visitorName")}
              value={value}
              onChange={onChange}
              error={errors?.[name]?.message}
              required
            />
          )}
        />
        <Controller
          name="companyName"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label={t("visitorCompanyName")}
              value={value}
              onChange={onChange}
              error={errors?.[name]?.message}
              required={!isVip}
            />
          )}
        />

        <Controller
          name="number"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <div className="flex flex-col">
              <TextInput
                label={t("mobileNumber")}
                value={value}
                onChange={onChange}
                className="input-fields"
                error={errors?.[name]?.message}
                required={!isVip}
                isInfoToolTip
                infoToolTip={<p>{t("enterPhoneNumber")}</p>}
              />
            </div>
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <TextInput
              label={t("email")}
              value={value}
              onChange={onChange}
              error={errors?.[name]?.message}
              required
            />
          )}
        />
        <Controller
          name="nationality"
          control={control}
          render={({ field: { name, onChange, value } }) => {
            return (
              <ComboboxInput
                options={nationalities}
                value={
                  value
                    ? nationalities?.find((item) => item.value === value)
                        ?.labelEn
                    : ""
                }
                onChange={onChange}
                error={errors?.[name] && t("nationalityRequired")}
                label={t("nationality")}
                required={!isVip}
              />
            );
          }}
        />
        <Controller
          name="idType"
          control={control}
          render={({ field: { name, onChange, value } }) => {
            return (
              <ComboboxInput
                options={documentIDTypes}
                onChange={(value) => onChange(value === "" ? null : value)}
                error={errors?.[name] && t("idTypeRequired")}
                label={t("idType")}
                required={!isVip}
                value={
                  String(value) || value === DOC_TYPE_ID.NATIONAL_ID
                    ? "National ID"
                    : ""
                }
              />
            );
          }}
        />
        {selectedIdType === DOC_TYPE_ID.IQAMA && (
          <>
            <Controller
              name="iqama"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("iqamaNumber")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                  isInfoToolTip
                  infoToolTip={<p>{t("enterIqamaNumber")}</p>}
                />
              )}
            />
            <Controller
              name="iqamaExpiryDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DatePickerInput
                  label={t("iqamaExpiryDate")}
                  value={value || ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                  minDate={
                    location.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                      ? new Date()
                      : undefined
                  }
                />
              )}
            />
          </>
        )}

        {(!selectedIdType || selectedIdType === DOC_TYPE_ID.NATIONAL_ID) && (
          <>
            <Controller
              name="eid"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("nationalID")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                  isInfoToolTip
                  infoToolTip={<p>{t("enterNationalId")}</p>}
                />
              )}
            />
            <Controller
              name="eidExpiryDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DatePickerInput
                  label={t("nationalIDExpiryDate")}
                  value={value || ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                  minDate={
                    location.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                      ? new Date()
                      : undefined
                  }
                />
              )}
            />
          </>
        )}

        {selectedIdType === DOC_TYPE_ID.PASSPORT && (
          <>
            <Controller
              name="passportNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("passportNumber")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                />
              )}
            />
            <Controller
              name="passportExpiryDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DatePickerInput
                  label={t("passportExpiryDate")}
                  value={value || ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                  minDate={
                    location.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                      ? new Date()
                      : undefined
                  }
                />
              )}
            />
          </>
        )}

        {selectedIdType === DOC_TYPE_ID.VISA && (
          <>
            <Controller
              name="visaNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("visaNumber")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                />
              )}
            />
            <Controller
              name="visaExpiryDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DatePickerInput
                  label={t("visaExpiryDate")}
                  value={value || ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                  minDate={
                    location.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                      ? new Date()
                      : undefined
                  }
                />
              )}
            />
          </>
        )}

        {selectedIdType === DOC_TYPE_ID.OTHER && (
          <>
            <Controller
              name="idTypeOtherName"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("documentNameOther")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                />
              )}
            />
            <Controller
              name="idTypeOtherNumber"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("documentNumberOther")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                />
              )}
            />
            <Controller
              name="idTypeOtherExpiryDate"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <DatePickerInput
                  label={t("documentExpiryDateOther")}
                  value={value || ""}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVip}
                  minDate={
                    location.pathname === APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                    location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                      ? new Date()
                      : undefined
                  }
                />
              )}
            />
          </>
        )}

        <Controller
          name="dateOfBirth"
          control={control}
          render={({ field: { name, value, onChange } }) => (
            <DatePickerInput
              label={t("dateOfBirth")}
              value={value || ""}
              onChange={onChange}
              error={errors?.[name]?.message}
              required={!isVip}
              maxDate={new Date()}
            />
          )}
        />

        {/* <Controller
          name="vehicleNo"
          control={control}
          render={({ field: { onChange, name, value, value } }) => (
            <TextInput
              label={t("vehicleNo")}
             value={value}
              onChange={onChange}
              error={errors?.[name]?.message}
              placeholder={i18n.language === "en" ? "799 LJK" : "ك ج ل ٧٩٩"}
            />
          )}
        /> */}

        {!selectedIdType && (
          <>
            <div />
            <div />
          </>
        )}
      </div>

      <AttachmentsGroupCard
        control={control}
        errors={errors}
        selectedIdType={selectedIdType}
        uploadPhotoRef={uploadPhotoRef}
        photoFileName={photoFileName}
        setPhotoFileName={setPhotoFileName}
        setPhotoFileType={setPhotoFileType}
        uploadEidRef={uploadEidRef}
        eidFileName={eidFileName}
        setEidFileName={setEidFileName}
        setEidFileType={setEidFileType}
        uploadPassportRef={uploadPassportRef}
        passportFileName={passportFileName}
        setPassportFileName={setPassportFileName}
        setPassportFileType={setPassportFileType}
        uploadVisaRef={uploadVisaRef}
        visaFileName={visaFileName}
        setVisaFileName={setVisaFileName}
        setVisaFileType={setVisaFileType}
        isVip={isVip}
        uploadVehicleRegLicenseRef={uploadVehicleRegLicenseRef}
        setVehicleRegLicenseFileName={setVehicleRegLicenseFileName}
        setVehicleRegLicenseFileType={setVehicleRegLicenseFileType}
        vehicleRegLicenseFileName={vehicleRegLicenseFileName}
        uploadHostEmployeeCardRef={uploadHostEmployeeCardRef}
        hostEmployeeCardFileName={hostEmployeeCardFileName}
        setHostEmployeeCardFileName={setHostEmployeeCardFileName}
        setHostEmployeeCardFileType={setHostEmployeeCardFileType}
        uploadIqamaRef={uploadIqamaRef}
        iqamaFileName={iqamaFileName}
        setIqamaFileName={setIqamaFileName}
        setIqamaFileType={setIqamaFileType}
        uploadIdOtherRef={uploadIdOtherRef}
        idOtherFileName={idOtherFileName}
        setIdOtherFileName={setIdOtherFileName}
        setIdOtherFileType={setIdOtherFileType}
        editingVisitor={editingVisitor}
        isAttachments={isAttachments}
        onAttachmentRemoved={(info) =>
          setIsAttachments((prev) => ({
            ...prev,
            [info.S_FileFieldName]: 0,
          }))
        }
      />

      <div className="my-4">
        <CheckboxInput
          key="remember-me"
          id="remember-me"
          label={t("declareDevicesBroughtOnsite")}
          checked={isDeviceDetails}
          setChecked={() => setIsDeviceDetails?.((prev) => !prev)}
        />
      </div>

      {isDeviceDetails && (
        <GroupVisitorDeviceDetailsCard
          addedDevices={addedDevices}
          setAddedDevices={setAddedDevices}
          clearErrors={clearErrors}
        />
      )}

      {isDeviceDetails && (
        <div className="h-[1px] w-full bg-gray-300 mt-4 mb-2" />
      )}

      <div className="flex items-center justify-between">
        <div />
        <div className="flex items-center gap-2">
          <Button
            isLoading={isValidatePhotoLoading}
            type="button"
            onClick={handleSubmit(onSubmit)}
          >
            {t("save")}
          </Button>
          <Button
            onClick={() => setIsAddVisitorFormVisible(false)}
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-purple-primary/10"
          >
            {t("cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VisitorDetailsGroupCard;
